/* eslint-disable camelcase */
import React, { useMemo, useState, useEffect, useCallback } from "react";
import { IdType, Row } from "react-table";

import {
  HvGlobalActions,
  HvGrid,
  HvTypography,
  HvInput,
  HvDropdown,
  ListValueProp,
} from "@hitachivantara/uikit-react-core";
import { useSops, IncidentTypeFormData } from "lib/api";

import SimpleTable, {
  textColumn,
  DEFAULT_PAGE_SIZE,
  DEFAULT_PAGE,
} from "components/common/SimpleTable";
import {
  HvTableColumnConfig,
  useHvTable,
  useHvSortBy,
  useHvPagination,
  useHvRowSelection,
  useHvBulkActions,
} from "@hitachivantara/uikit-react-lab";
import { useTranslation } from "react-i18next";
import { useFormikContext } from "formik";
import useStyles from "./styles";

interface SopDataRow extends Record<string, unknown> {
  id: number;
  name: string;
  description: string;
  step: SopStep[];
}

const DEFAULT_SORT = [
  {
    id: "name",
    desc: false,
  },
];

const IncidentTypeSop: React.FC = () => {
  const { t } = useTranslation("addEditIncidentType");

  const {
    setFieldValue,
    setFieldTouched,
    values: { inc_type_sop_map },
  } = useFormikContext<IncidentTypeFormData>();

  const classes = useStyles();
  const [searchKey, setSearchKey] = useState<string>("");
  const [dropdownFilter, setDropdownFilter] = useState<string>("all");
  const [tableData, setTableData] = useState<SopDataRow[]>([]);

  const [totalCount, setTotalCount] = useState(0);

  const [page, setPage] = useState(0);
  const [size, setSize] = useState(DEFAULT_PAGE_SIZE);
  const [selectedIds, setSelectedIds] = useState<
    Record<IdType<SopDataRow>, boolean>
  >(() =>
    Object.fromEntries(new Map(inc_type_sop_map.map((v: number) => [v, true])))
  );

  const handleSearch = useCallback((event, value) => {
    event.preventDefault();
    setSearchKey(value);
    setPage(DEFAULT_PAGE);
  }, []);

  const sopCountSort = useCallback(
    (rowA: Row<SopDataRow>, rowB: Row<SopDataRow>) => {
      if (rowA.original.step.length < rowB.original.step.length) return -1;
      if (rowA.original.step.length > rowB.original.step.length) return 1;
      return 0;
    },
    []
  );

  const columns = useMemo(
    (): HvTableColumnConfig<SopDataRow>[] => [
      textColumn({
        Header: t("sop.columns.name") as string,
        accessor: "name",
        style: { width: "30%" },
        sortType: "string",
      }),
      textColumn({
        Header: t("sop.columns.step") as string,
        accessor: "step",
        style: { width: "10%" },
        sortType: sopCountSort,
        Cell: (data: { row: { original: { step: [] } } }) =>
          data.row.original.step.length,
      }),
      textColumn({
        Header: t("sop.columns.description") as string,
        accessor: "description",
        style: { width: "60%" },
        sortType: "string",
      }),
    ],
    [t, sopCountSort]
  );
  const { data, error } = useSops();

  useEffect(() => {
    if (data) {
      const newData = data.data
        .filter(
          ({ sop_name, sop_desc }) =>
            sop_name.toLowerCase().includes(searchKey.toLowerCase()) ||
            sop_desc.toLowerCase().includes(searchKey.toLowerCase())
        )
        .filter(({ pk_sop_template_id }) => {
          if (dropdownFilter === "selected") {
            if (inc_type_sop_map.includes(pk_sop_template_id)) {
              return true;
            }
            return false;
          }
          return true;
        })
        .map((sop) => ({
          id: sop.pk_sop_template_id,
          name: sop.sop_name,
          description: sop.sop_desc,
          step: sop.sop_steps,
        }));

      setTableData(newData);
      setTotalCount(newData.length);
    }
  }, [data, dropdownFilter, inc_type_sop_map, searchKey]);

  const labels = useMemo(
    () => ({
      selectRowCheckBoxAriaLabel: t("labels.selectCheckbox"),
    }),
    [t]
  );

  const onFilterOptionChange = useCallback(
    (selection: ListValueProp | ListValueProp[] | undefined) => {
      const selectedValue = selection as ListValueProp | undefined;
      setDropdownFilter(selectedValue?.id || "all");
      setPage(0);
    },
    []
  );

  const initialState = useMemo(() => {
    return {
      pageIndex: page,
      pageSize: size,
      selectedRowIds: selectedIds,
      sortBy: DEFAULT_SORT,
    };
  }, [page, size, selectedIds]);

  const instance = useHvTable<SopDataRow>(
    {
      columns,
      data: tableData || [],
      getRowId: useCallback((v: SopDataRow): string => `${v.id}`, []),
      pageCount: Math.ceil((totalCount ?? 0) / (initialState?.pageSize ?? 1)),
      initialState,
      labels,
      aditivePageBulkSelection: true,
      subtractivePageBulkDeselection: true,
      disableMultiSort: true,
      disableSortRemove: true,
      disableCreateExpandButton: true,
    },
    useHvSortBy,
    useHvPagination,
    useHvRowSelection,
    useHvBulkActions
  );

  const {
    state: { pageIndex, pageSize, selectedRowIds },
  } = instance;

  useEffect(() => setPage?.(pageIndex), [pageIndex, setPage]);
  useEffect(() => setSize?.(pageSize), [pageSize, setSize]);
  useEffect(() => {
    setSelectedIds(selectedRowIds);
    setFieldValue(
      "inc_type_sop_map",
      Object.keys(selectedRowIds).map((v) => Number(v)),
      false
    );
    setFieldTouched("inc_type_sop_map", true, false);
  }, [selectedRowIds, setFieldValue, setFieldTouched]);

  return (
    <HvGrid container>
      <HvGrid item sm={12}>
        <HvGlobalActions title={t("sop.sectionTitle")} variant="section" />
        <HvTypography variant="normalText">
          {t("sections.sop.description")}
        </HvTypography>
      </HvGrid>
      <HvGrid item container xs={12} spacing={0}>
        <HvGrid item xs={6}>
          <HvInput
            className={classes.search}
            type="search"
            aria-label={t("common:search.ariaLabel")}
            placeholder={t("sop.search")}
            onEnter={handleSearch}
          />
        </HvGrid>
        <HvGrid item xs={6}>
          <HvDropdown
            className={classes.dropdown}
            onChange={onFilterOptionChange}
            values={["all", "selected"].map((val) => ({
              id: `${val}`,
              label: t(`sop.dropdown.${val}`),
              selected: val === dropdownFilter,
            }))}
          />
        </HvGrid>
      </HvGrid>
      <HvGrid item md={12}>
        <SimpleTable
          instance={instance}
          totalCount={instance.rows.length}
          loading={(!data && !error) || tableData === []}
          labels={{
            noData: t("sop.noData"),
            selectAllPagesLabel:
              searchKey !== ""
                ? t("sop.filteredSelectAllPages", {
                    totalCount,
                  })
                : "",
          }}
        />
      </HvGrid>
    </HvGrid>
  );
};

export default IncidentTypeSop;
